import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Twitter, Linkedin, Instagram, 
  Globe, Heart, Facebook, MessageCircle, Star, 
  ArrowRight, ChevronRight, Sparkles, Building, 
  Users, Shield, Award, Zap, Clock, Coffee,
  ArrowUp, ExternalLink, CheckCircle
} from 'lucide-react';
import { COMPANY_INFO } from '../utils/constants';

export default function Footer() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);

  // Update year on mount
  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  // Handle scroll to show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Quick links with icons
  const quickLinks = [
    { to: '/about', label: 'About Us', icon: <Building className="w-4 h-4" /> },
    { to: '/services', label: 'Services', icon: <Zap className="w-4 h-4" /> },
    { to: '/portfolio', label: 'Portfolio', icon: <Award className="w-4 h-4" /> },
    { to: '/careers', label: 'Careers', icon: <Users className="w-4 h-4" /> },
    { to: '/blog', label: 'Blog', icon: <MessageCircle className="w-4 h-4" /> },
    { to: '/contact', label: 'Contact', icon: <Phone className="w-4 h-4" /> }
  ];

  // Community links
  const communityLinks = [
    { to: '/forum', label: 'Forum & Chat', icon: <MessageCircle className="w-4 h-4" /> },
    { to: '/feedback', label: 'Customer Feedback', icon: <Star className="w-4 h-4" /> }
  ];

  // Services links
  const serviceLinks = [
    { to: '/services/software-development', label: 'Software Development' },
    { to: '/services/cloud-computing', label: 'Cloud Computing' },
    { to: '/services/cyber-security', label: 'Cyber Security' },
    { to: '/services/vlsi-design', label: 'VLSI Design' },
    { to: '/services/ai-ml-solutions', label: 'AI & ML Solutions' },
    { to: '/services/data-analytics', label: 'Data Analytics' },
    { to: '/services/blockchain', label: 'Blockchain & Web3' },
    { to: '/services/game-development', label: 'Game Development' }
  ];

  // Social media links with gradients
  const socialLinks = [
    { 
      href: COMPANY_INFO.social?.facebook || '#', 
      icon: <Facebook className="w-5 h-5" />, 
      label: 'Facebook',
      gradient: 'from-blue-600 to-blue-800'
    },
    { 
      href: COMPANY_INFO.social?.twitter || '#', 
      icon: <Twitter className="w-5 h-5" />, 
      label: 'Twitter',
      gradient: 'from-sky-500 to-blue-500'
    },
    { 
      href: COMPANY_INFO.social?.linkedin || '#', 
      icon: <Linkedin className="w-5 h-5" />, 
      label: 'LinkedIn',
      gradient: 'from-blue-700 to-blue-900'
    },
    { 
      href: COMPANY_INFO.social?.instagram || '#', 
      icon: <Instagram className="w-5 h-5" />, 
      label: 'Instagram',
      gradient: 'from-purple-600 to-pink-600'
    }
  ];

  // Credentials data
  const credentials = [
    { icon: '🇮🇳', label: 'DPIIT Startup India', value: COMPANY_INFO.credentials?.startupIndia },
    { icon: '📋', label: 'MSME Registered', value: COMPANY_INFO.credentials?.msme },
    { icon: '🏢', label: 'CIN', value: COMPANY_INFO.credentials?.cin },
    { icon: '💰', label: 'GSTIN', value: COMPANY_INFO.credentials?.gstin }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 
                      dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 text-white 
                      overflow-hidden z-10 mt-20">
      
      {/* Top Gradient Border */}
      <div className="h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 
                      animate-pulse w-full"></div>
      
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      {/* Back to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 
                     text-white rounded-full flex items-center justify-center shadow-2xl 
                     hover:shadow-3xl hover:scale-110 active:scale-95 transition-all duration-300 
                     animate-fade-in-up"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12 mb-12 md:mb-16">
          
          {/* Company Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo and Company Name */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-purple-600 
                               rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/20 
                               transform hover:scale-105 transition-transform duration-300">
                  <img
                    src="/logo/company-logo.png"
                    alt={`${COMPANY_INFO.name} Logo`}
                    className="w-12 h-12 md:w-14 md:h-14 object-contain"
                  />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 
                               rounded-full flex items-center justify-center text-xs font-bold animate-pulse">
                  🚀
                </div>
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 
                              bg-clip-text text-transparent">
                  {COMPANY_INFO.name}
                </h3>
                <p className="text-sm text-gray-400">Technology Innovators</p>
              </div>
            </div>

            {/* Auto-scrolling Company Descriptions */}
            <div className="h-40 overflow-hidden relative mt-4">
              <div className="absolute top-0 left-0 w-full space-y-4 animate-vertical-scroll">
                {[...COMPANY_INFO.descriptions, ...COMPANY_INFO.descriptions].map((desc, index) => (
                  <p key={index} className="text-gray-300 leading-relaxed text-sm md:text-base max-w-xl">
                    {desc}
                  </p>
                ))}
              </div>
            </div>

            {/* Mission Badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-900/30 to-purple-900/30 
                          backdrop-blur-sm p-4 rounded-xl border border-blue-500/20">
              <span className="text-3xl">🇮🇳</span>
              <div>
                <p className="font-bold text-blue-300">Making India Self-Reliant</p>
                <p className="text-xs text-gray-400">Atmanirbhar Bharat Initiative</p>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="pt-4">
              <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Follow Our Journey
              </h4>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group relative w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br ${social.gradient} 
                               rounded-xl flex items-center justify-center text-white shadow-lg 
                               hover:shadow-2xl hover:scale-110 transition-all duration-300`}
                    aria-label={social.label}
                    onMouseEnter={() => setHoveredLink(`social-${index}`)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    <div className="absolute inset-0 bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10 transform group-hover:scale-110 transition-transform">
                      {social.icon}
                    </div>
                    {hoveredLink === `social-${index}` && (
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 
                                    text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap">
                        {social.label}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-500" />
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.to}
                    className="group flex items-center gap-3 text-gray-300 hover:text-white 
                             transition-all duration-300 hover:translate-x-2 py-2"
                    onMouseEnter={() => setHoveredLink(`quick-${index}`)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-900/30 to-purple-900/30 
                                   rounded-lg flex items-center justify-center group-hover:from-blue-600 
                                   group-hover:to-purple-600 transition-all">
                      {link.icon}
                    </div>
                    <span className="flex-1 text-sm md:text-base">{link.label}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 
                                            group-hover:translate-x-1 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>

            {/* Community Links */}
            <div className="pt-6 border-t border-gray-700">
              <h5 className="font-semibold mb-3 text-blue-300">Community</h5>
              <div className="space-y-3">
                {communityLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.to}
                    className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-900/30 to-pink-900/30 
                                   rounded-lg flex items-center justify-center">
                      {link.icon}
                    </div>
                    <span className="text-sm">{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.to}
                    className="group flex items-center gap-2 text-gray-400 hover:text-white 
                             transition-all duration-300 py-1.5"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 
                                          group-hover:translate-x-1 transition-all" />
                    <span className="text-sm truncate">{service.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 
                       font-semibold text-sm mt-4 group"
            >
              <span>View All Services</span>
              <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Contact Info Column */}
          <div className="space-y-6">
            <h4 className="text-xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-green-500" />
              Contact Info
            </h4>
            <div className="space-y-4">
              {/* Address */}
              <div className="flex items-start gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-900/30 to-blue-900/50 
                               rounded-lg flex items-center justify-center group-hover:from-blue-600 
                               group-hover:to-blue-700 transition-all mt-1">
                  <MapPin className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Our Office</p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {COMPANY_INFO.contact?.address?.full || COMPANY_INFO.address || 
                     'Bangalore, Karnataka, India'}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-green-900/30 to-green-900/50 
                               rounded-lg flex items-center justify-center group-hover:from-green-600 
                               group-hover:to-green-700 transition-all">
                  <Phone className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Call Us</p>
                  <a 
                    href={`tel:${COMPANY_INFO.phone || COMPANY_INFO.contact?.phone}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {COMPANY_INFO.phone || COMPANY_INFO.contact?.phone || '+91-XXXXXXXXXX'}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-900/30 to-orange-900/50 
                               rounded-lg flex items-center justify-center group-hover:from-orange-600 
                               group-hover:to-orange-700 transition-all">
                  <Mail className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Email Us</p>
                  <a 
                    href={`mailto:${COMPANY_INFO.email || COMPANY_INFO.contact?.email}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm break-all"
                  >
                    {COMPANY_INFO.email || COMPANY_INFO.contact?.email || 'info@nevxoaulio.com'}
                  </a>
                </div>
              </div>

              {/* Business Hours */}
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-900/30 to-purple-900/50 
                               rounded-lg flex items-center justify-center group-hover:from-purple-600 
                               group-hover:to-purple-700 transition-all">
                  <Clock className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Business Hours</p>
                  <p className="text-gray-400 text-sm">Mon - Sat: 9 AM - 6 PM</p>
                  <p className="text-gray-500 text-xs">Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Credentials & Awards - Compact Mobile Version */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <h5 className="text-lg font-semibold mb-6 text-center">Our Credentials</h5>
          <div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-3 lg:grid-cols-4 lg:gap-4">
            {credentials.map((cred, index) => (
              cred.value && (
                <div 
                  key={index}
                  className="relative bg-gradient-to-br from-gray-800 to-gray-900 p-3 rounded-xl 
                            border border-gray-700 flex items-center min-h-[70px]"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex-shrink-0">
                      <span className="text-xl">{cred.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5 truncate">{cred.label}</p>
                      <div className="relative">
                        <p className="text-xs font-medium truncate peer"
                          onClick={(e) => {
                            if (window.innerWidth < 1024) {
                              const text = e.currentTarget;
                              text.classList.toggle('truncate');
                              text.classList.toggle('break-all');
                              text.classList.toggle('whitespace-normal');
                            }
                          }}>
                          {cred.value}
                        </p>
                        <div className="hidden lg:group-hover:block absolute top-full left-0 right-0 
                                      bg-gray-900 text-white text-xs p-2 rounded-md shadow-lg 
                                      border border-gray-700 z-20 break-all whitespace-normal mt-1">
                          {cred.value}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Copyright & Legal */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Heart className="w-5 h-5 text-red-500 fill-red-500 animate-pulse" />
                <span className="text-gray-400 text-sm">
                  Made with love in India 🇮🇳
                </span>
              </div>
              <p className="text-gray-500 text-xs">
                © {year} {COMPANY_INFO.name}. All rights reserved.
              </p>
              <p className="text-gray-600 text-xs mt-1">
                Empowering businesses through technology innovation.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link 
                to="/privacy" 
                className="text-gray-400 hover:text-white transition-colors hover:scale-105"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-400 hover:text-white transition-colors hover:scale-105"
              >
                Terms of Service
              </Link>
              <Link 
                to="/sitemap" 
                className="text-gray-400 hover:text-white transition-colors hover:scale-105"
              >
                Sitemap
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-blue-500" />
                <span>Verified</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Coffee className="w-4 h-4 text-yellow-500" />
                <span>100% Uptime</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black/50 py-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs text-gray-500">
            <p>
              {COMPANY_INFO.name} is a DPIIT recognized startup under the Startup India initiative. 
              We are committed to building a self-reliant India through technology innovation.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes verticalScroll {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-50%);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-vertical-scroll {
          animation: verticalScroll 40s linear infinite;
        }

        /* Smooth hover effects */
        a, button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Mobile optimization */
        @media (max-width: 768px) {
          .grid-cols-5 {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          
          .text-3xl {
            font-size: 2rem;
          }
          
          .text-2xl {
            font-size: 1.75rem;
          }
        }

        /* Reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in-up,
          .animate-vertical-scroll,
          .transition-all,
          .transition-transform,
          .transition-opacity,
          .transition-colors {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </footer>
  );
}